import {
  countServiceMinutes,
  getParentSaleLines,
  getParentSaleLinesForDisplay,
  toPosTotals,
} from '@/components/pos/posCart'
import { canPrintServiceSlipsFromSale } from '@/components/pos/posServiceSlip'
import { PosCartLineEditor } from '@/components/pos/PosCartLineEditor'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { Printer } from 'lucide-react'
import { POS_PANEL_IDS } from './posViewport'

/**
 * @param {object} props
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {import('@/types/api').Stylist[]} props.stylists
 * @param {boolean} props.loading
 * @param {boolean} props.mutating
 * @param {string} props.error
 * @param {() => void | Promise<void>} [props.onPrintServiceSlips]
 * @param {number | null} [props.selectedLineId]
 * @param {(lineId: number | null) => void} [props.onSelectLine]
 */
export function PosCartPanel({
  sale,
  stylists,
  loading,
  mutating,
  error,
  onPrintServiceSlips,
  selectedLineId = null,
  onSelectLine,
}) {
  const { t } = useUiLocale()
  const parentLines = getParentSaleLines(sale)
  const displayLines = getParentSaleLinesForDisplay(sale)
  const totals = toPosTotals(sale)
  const totalMinutes = countServiceMinutes(parentLines)
  const canPrintSlips = canPrintServiceSlipsFromSale(sale)

  return (
    <section
      id={POS_PANEL_IDS.cart}
      className="pos-v2-ticket flex min-h-0 flex-col overflow-hidden bg-white"
      aria-label="Ticket and cart"
    >
      <div className="pos-v2-ticket-header flex shrink-0 items-center justify-between gap-2 border-b border-[#E5E7EB] bg-[#F8FAFC] px-2">
        <p className="pos-v2-ticket-header-title truncate font-bold text-slate-800">
          {loading
            ? t('common.loading')
            : t('pos.ticketSummary', { lines: parentLines.length, minutes: totalMinutes })}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <p className="pos-v2-ticket-header-total font-mono font-bold text-slate-900">
            AED {totals.grand_total.toFixed(2)}
          </p>
          {onPrintServiceSlips ? (
            <button
              type="button"
              disabled={mutating || !canPrintSlips}
              onClick={onPrintServiceSlips}
              className="flex h-6 w-6 items-center justify-center rounded border border-[#E5E7EB] bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              aria-label="Print stylist slips"
              title="Print stylist slips"
            >
              <Printer className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="pos-v2-ui-meta shrink-0 border-b border-destructive/20 bg-destructive/5 px-2 py-0.5 text-destructive">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="space-y-0.5 p-1">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="pos-v2-ticket-line skeleton-shimmer" aria-hidden />
          ))}
        </div>
      ) : displayLines.length === 0 ? (
        <p className="pos-v2-ui-body shrink-0 px-2 py-1 text-slate-500">
          Tap a service below to start.
        </p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="pos-v2-ticket-head pos-v2-ticket-head--compact shrink-0">
            <span>Service</span>
            <span>Staff</span>
            <span>Qty</span>
            <span>Total</span>
          </div>
          <ul className="pos-v2-ticket-list">
            {displayLines.map((line) => (
              <PosCartLineEditor
                key={line.id}
                line={line}
                stylists={stylists}
                disabled={mutating}
                selected={selectedLineId != null && line.id == selectedLineId}
                onSelect={onSelectLine ? () => onSelectLine(line.id) : undefined}
                compact
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
